import React, { useEffect, useRef, useState } from 'react';
import { FiSmartphone, FiWifi, FiWifiOff, FiCheckCircle, FiRefreshCw, FiLoader } from 'react-icons/fi';
import api from '../../../services/api';

const POLL_MS = 3000;

export default function WhatsappConfig() {
  const [data, setData] = useState(null); // { status, qr, phone, name, platform }
  const [disconnecting, setDisconnecting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/api/whatsapp/status');
      setData(res.data.data);
    } catch (_) {
      // silencioso — el próximo poll reintenta
    }
  };

  // No se llama a /init automáticamente al entrar a esta pantalla — el QR
  // solo se genera con un click explícito (botón "Generar código QR"), para
  // no levantar Chromium ni mostrar un código que nadie pidió cada vez que
  // alguien abre esta página. Acá solo se consulta el estado actual.
  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleGenerarQr = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api/whatsapp/init');
      setData(res.data.data);
    } catch (_) {
      // el próximo poll refleja el estado real
    } finally {
      setGenerating(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await api.post('/api/whatsapp/disconnect');
      setData(res.data.data);
    } catch (_) {
      // el próximo poll refleja el estado real
    } finally {
      setDisconnecting(false);
    }
  };

  const status = data?.status || 'idle';

  return (
    <div style={{ padding: 20, maxWidth: 480 }}>
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16,
        padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, background: '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FiSmartphone size={20} color="#16a34a" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0d1b2e' }}>Vinculación WhatsApp</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Conecta el número que enviará los comprobantes</div>
          </div>
        </div>

        {status === 'idle' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '28px 0' }}>
            <FiSmartphone size={32} color="#cbd5e1" />
            <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center' }}>No hay ninguna sesión activa</div>
            <button
              onClick={handleGenerarQr}
              disabled={generating}
              style={{
                padding: '9px 18px', borderRadius: 10, border: 'none',
                background: '#16a34a', color: '#fff', fontSize: 12.5, fontWeight: 600,
                cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.7 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {generating ? <FiLoader size={14} className="wa-spin" /> : <FiRefreshCw size={14} />}
              {generating ? 'Generando...' : 'Generar código QR'}
            </button>
          </div>
        )}

        {status === 'initializing' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '32px 0', color: '#94a3b8' }}>
            <FiLoader size={32} className="wa-spin" color="#16a34a" />
            <div style={{ fontSize: 13 }}>Generando código QR...</div>
            <div style={{ fontSize: 11, color: '#cbd5e1' }}>Esto puede tardar unos segundos</div>
          </div>
        )}

        {status === 'qr' && data?.qr && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ padding: 10, background: '#fff', border: '2px solid #bbf7d0', borderRadius: 16 }}>
              <img src={data.qr} alt="QR WhatsApp" width={208} height={208} style={{ borderRadius: 10 }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Escanea este código con tu celular</div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#b45309',
              background: '#fffbeb', padding: '6px 12px', borderRadius: 10, border: '1px solid #fde68a',
            }}>
              <FiRefreshCw size={13} /> Escanéalo pronto: caduca solo si nadie lo usa
            </div>
          </div>
        )}

        {status === 'qr_expired' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '28px 0' }}>
            <FiWifiOff size={28} color="#f59e0b" />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>El código QR caducó</div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>Nadie lo escaneó a tiempo</div>
            </div>
            <button
              onClick={handleGenerarQr}
              disabled={generating}
              style={{
                padding: '9px 18px', borderRadius: 10, border: 'none',
                background: '#16a34a', color: '#fff', fontSize: 12.5, fontWeight: 600,
                cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.7 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {generating ? <FiLoader size={14} className="wa-spin" /> : <FiRefreshCw size={14} />}
              {generating ? 'Generando...' : 'Generar nuevo código'}
            </button>
          </div>
        )}

        {status === 'connected' && (
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: 14, borderRadius: 14,
              background: '#f0fdf4', border: '1px solid #bbf7d0',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', background: '#22c55e',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <FiCheckCircle size={22} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Vinculado</span>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
                </div>
                {data.name && <div style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{data.name}</div>}
                {data.phone && <div style={{ fontSize: 11.5, color: '#64748b' }}>+{data.phone}</div>}
              </div>
              <FiWifi size={18} color="#22c55e" />
            </div>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{
                width: '100%', marginTop: 14, padding: '9px 0', borderRadius: 10,
                border: '1px solid #fecaca', color: '#dc2626', background: '#fff',
                fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <FiWifiOff size={14} /> {disconnecting ? 'Desvinculando...' : 'Desvincular y reconectar'}
            </button>
          </div>
        )}

        {status === 'disconnected' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0', color: '#94a3b8' }}>
            <FiWifiOff size={28} color="#cbd5e1" />
            <div style={{ fontSize: 13, color: '#64748b' }}>Sin conexión</div>
            <div style={{ fontSize: 11, color: '#cbd5e1' }}>El servicio se reconectará automáticamente en breve...</div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes wa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .wa-spin { animation: wa-spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
